import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Provider,
  ProviderResponse,
  ProviderWebsite,
  ProviderContact,
  ProviderLogin,
  CreateOrUpdateProviderWebsiteDTO,
  CreateOrUpdateProviderContactDTO,
  CreateOrUpdateProviderLoginDTO,
  UpdateProviderLoginActiveFlagDTO,
  ProviderLoginPasswordDTO,
  ProviderWebsiteListDTO,
  ProviderContactListDTO,
  ProviderLoginListDTO,
} from '../model/provider.model';
import { environment } from '../../src/environments/environment';

export interface CreateProviderDTO {
  providerName: string;
  providerDisplayName: string;
  providerServiceStartDate: string | null;
  providerServiceEndDate: string | null;
}

export interface UpdateProviderDTO {
  providerName: string;
  providerDisplayName: string;
  providerServiceStartDate: string | null;
  providerServiceEndDate: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  private readonly baseUrl = environment.apiBaseAdminUrl;

  constructor(private http: HttpClient) {}

  getProviders(): Observable<Provider[]> {
    return this.http.get<ProviderResponse>(`${this.baseUrl}/providers`).pipe(map(response => response.data));
  }

  createProvider(body: CreateProviderDTO): Observable<Provider> {
    return this.http.post<Provider>(`${this.baseUrl}/providers`, body);
  }

  updateProvider(providerId: number, body: UpdateProviderDTO): Observable<Provider> {
    return this.http.put<Provider>(`${this.baseUrl}/providers/${providerId}`, body);
  }

  getProviderWebsites(providerId: number): Observable<ProviderWebsite[]> {
    return this.http.get<ProviderWebsiteListDTO>(`${this.baseUrl}/providers/${providerId}/websites`).pipe(map(response => response.data));
  }

  getProviderWebsite(providerId: number, websiteId: number): Observable<ProviderWebsite> {
    return this.http.get<ProviderWebsite>(`${this.baseUrl}/providers/${providerId}/websites/${websiteId}`);
  }

  createProviderWebsite(providerId: number, websiteData: CreateOrUpdateProviderWebsiteDTO): Observable<ProviderWebsite> {
    return this.http.post<ProviderWebsite>(`${this.baseUrl}/providers/${providerId}/websites`, websiteData);
  }

  updateProviderWebsite(providerId: number, websiteId: number, websiteData: CreateOrUpdateProviderWebsiteDTO): Observable<ProviderWebsite> {
    return this.http.put<ProviderWebsite>(`${this.baseUrl}/providers/${providerId}/websites/${websiteId}`, websiteData);
  }

  deleteProviderWebsite(providerId: number, websiteId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/providers/${providerId}/websites/${websiteId}`);
  }

  getProviderContacts(providerId: number): Observable<ProviderContact[]> {
    return this.http.get<ProviderContactListDTO>(`${this.baseUrl}/providers/${providerId}/contacts`).pipe(map(response => response.data));
  }

  getProviderContact(providerId: number, contactId: number): Observable<ProviderContact> {
    return this.http.get<ProviderContact>(`${this.baseUrl}/providers/${providerId}/contacts/${contactId}`);
  }

  createProviderContact(providerId: number, contactData: CreateOrUpdateProviderContactDTO): Observable<ProviderContact> {
    return this.http.post<ProviderContact>(`${this.baseUrl}/providers/${providerId}/contacts`, contactData);
  }

  updateProviderContact(providerId: number, contactId: number, contactData: CreateOrUpdateProviderContactDTO): Observable<ProviderContact> {
    return this.http.put<ProviderContact>(`${this.baseUrl}/providers/${providerId}/contacts/${contactId}`, contactData);
  }

  deleteProviderContact(providerId: number, contactId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/providers/${providerId}/contacts/${contactId}`);
  }

  getProviderLogins(providerId: number): Observable<ProviderLogin[]> {
    return this.http.get<ProviderLoginListDTO>(`${this.baseUrl}/providers/${providerId}/logins`).pipe(map(response => response.data));
  }

  getProviderLogin(providerId: number, loginId: number): Observable<ProviderLogin> {
    return this.http.get<ProviderLogin>(`${this.baseUrl}/providers/${providerId}/logins/${loginId}`);
  }

  createProviderLogin(providerId: number, loginData: CreateOrUpdateProviderLoginDTO): Observable<ProviderLogin> {
    return this.http.post<ProviderLogin>(`${this.baseUrl}/providers/${providerId}/logins`, loginData);
  }

  updateProviderLogin(providerId: number, loginId: number, loginData: CreateOrUpdateProviderLoginDTO): Observable<ProviderLogin> {
    return this.http.put<ProviderLogin>(`${this.baseUrl}/providers/${providerId}/logins/${loginId}`, loginData);
  }

  updateProviderLoginActiveFlag(providerId: number, loginId: number, activeData: UpdateProviderLoginActiveFlagDTO): Observable<ProviderLogin> {
    return this.http.put<ProviderLogin>(`${this.baseUrl}/providers/${providerId}/logins/${loginId}/active`, activeData);
  }

  getProviderLoginPassword(providerId: number, loginId: number): Observable<ProviderLoginPasswordDTO> {
    return this.http.get<ProviderLoginPasswordDTO>(`${this.baseUrl}/providers/${providerId}/logins/${loginId}/password`);
  }

  deleteProviderLogin(providerId: number, loginId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/providers/${providerId}/logins/${loginId}`);
  }
}
